/* eslint-disable custom-rules/modal-and-drawer-requires-title */
/* eslint-disble react/jsx-sort-props */
import { useMediaQuery } from '@uhg-abyss/web/hooks/useMediaQuery';
import { Button } from '@uhg-abyss/web/ui/Button';
import { Drawer } from '@uhg-abyss/web/ui/Drawer';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { Popover } from '@uhg-abyss/web/ui/Popover';
import find from 'lodash/find';
import React, { useState } from 'react';

import useUpdatedCostIndicatorLabel from '../../frontends/ProviderSearch/routes/MapView/CompareDrawer/utility/editCompareDrawer.util';
import { useLagoon } from '../../hooks/useLagoon';
import { Provider } from '../../models/Provider';
import { ResponseHeaders } from '../../models/ResponseHeaders';
import { adobeLinkTrackEvent } from '../AdobeTagging/adobeLinkTrackEvent';
import { adobeModalTrackEvent } from '../AdobeTagging/adobeModalTrackEvent';
import { infoCostIconButtonMobile, phoneOnly } from '../ConstantsStyles';
import { CostIndicator } from '../CostIndicator/CostIndicator';
import {
  convertProviderTypeToAdobeType,
  formatProviderId,
  handleAdobeSearchResultClickAndModalEvent,
} from '../Utils/adobeTrackUtils/adobeTrackUtils';
import {
  CostIndicatorDrawerStyles,
  CostIndicatorDrawerTextStyles,
  PopoverCostIndicatorGroupLocationStyles,
  PopoverCostIndicatorStyles,
} from './CostIndicatorPopover.style';

type Props = {
  costIndicator?: string;
  practitioner?: Provider;
  displayCostIndicator?: boolean;
  callFrom?: string;
  indexForAnalytics?: number;
  locationForAnalytics?: string;
  providerType?: string;
  selectedFilters?: string;
  headers?: ResponseHeaders;
  popupContainer?: boolean;
  searchTerm?: string;
  enableMapViewHideOption?: boolean;
  isGroupedLocationDrawer?: boolean;
  isNewMapViewDataCardDesign?: boolean;
};

export const CostIndicatorPopover = ({
  popupContainer,
  costIndicator,
  displayCostIndicator = true,
  callFrom,
  indexForAnalytics,
  locationForAnalytics,
  selectedFilters,
  headers = { correlationId: '' },
  practitioner,
  providerType,
  searchTerm,
  enableMapViewHideOption = false,
  isGroupedLocationDrawer,
  isNewMapViewDataCardDesign = false,
}: Props) => {
  const mobileScreen = useMediaQuery(phoneOnly);
  const [CostIndicatorDrawerToggle, setCostIndicatorDrawerToggle] =
    useState(false);

  const lagoonCostIndicatorData = useLagoon('cost-indicator-popover')();
  const costIndicatorData = find(lagoonCostIndicatorData, { key: '1' });
  const costIndicatorContent = costIndicatorData?.costIndicatorPopoverContent;

  const getProviderName = (provider) => ({
    firstName: provider?.firstName,
    middleName: provider?.middleName,
    lastName: provider?.lastName,
  });

  const practitionerDetails = getProviderName(practitioner);

  const { label, id } = useUpdatedCostIndicatorLabel({
    costIndicator,
    practitioner: practitionerDetails,
  });

  const costIndicatorTitle = costIndicatorData?.title;

  const locationForAdobe = `body:details header`;

  const costIndicatorTestId: string = mobileScreen
    ? 'cost-indicator-drawer'
    : 'cost-indicator-popover';

  const linkTrack = () => {
    adobeLinkTrackEvent({
      name: 'cost indicator',
      location: locationForAdobe,
      type: 'internal',
    });
    adobeModalTrackEvent('cost indicator');
  };
  const searchTrack = () => {
    handleAdobeSearchResultClickAndModalEvent({
      filters: selectedFilters,
      linkName: costIndicatorTestId,
      linkPosition: indexForAnalytics,
      linkLocation: locationForAnalytics,
      modalName: 'cost indicator',
      customAttributesBlock: {
        correlationId: headers.correlationId,
        providerId: formatProviderId(practitioner),
        providerType: convertProviderTypeToAdobeType(providerType),
      },
      term: searchTerm ?? '',
    });
  };

  const trackAdobeEvent = () => {
    if (callFrom && callFrom === 'datacard') {
      searchTrack();
    } else {
      linkTrack();
    }
  };

  return (
    <React.Fragment>
      {!mobileScreen && !popupContainer ? (
        <div>
          <Popover
            aria-label={label}
            content={
              <div
                dangerouslySetInnerHTML={{
                  __html: costIndicatorContent,
                }}
              />
            }
            css={
              isGroupedLocationDrawer
                ? PopoverCostIndicatorGroupLocationStyles
                : PopoverCostIndicatorStyles(isNewMapViewDataCardDesign)
            }
            data-auto-testid={costIndicatorTestId}
            data-testid={costIndicatorTestId}
            id={id}
            name={label}
            onOpenChange={(open) => {
              if (open) {
                trackAdobeEvent();
              }
            }}
            tabIndex={0}
            title={costIndicatorTitle}
            width={220}
          >
            <Layout.Group space={4}>
              {displayCostIndicator && (
                <CostIndicator
                  enableMapViewHideOption={enableMapViewHideOption}
                  indicatorData={costIndicator || ''}
                  isGroupedLocationDrawer={isGroupedLocationDrawer}
                  isNewMapViewDataCardDesign={isNewMapViewDataCardDesign}
                />
              )}
              {!enableMapViewHideOption && (
                <Button
                  css={{
                    display: 'inline-block',
                    padding: '0px',
                    border: 'none',
                    height: 'fit-content',
                  }}
                  variant="outline"
                >
                  <IconSymbol
                    data-auto-testid="cost-indicator-popover-info-icon"
                    data-testid="cost-indicator-popover-info-icon"
                    icon="info"
                    size={16}
                    variant="outlined"
                  />
                </Button>
              )}
            </Layout.Group>
            <p aria-hidden="true" style={{ display: 'none' }}>
              {label}
            </p>
          </Popover>
        </div>
      ) : (
        <React.Fragment>
          <Drawer
            css={CostIndicatorDrawerStyles}
            data-auto-testid={costIndicatorTestId}
            data-testid={costIndicatorTestId}
            header={costIndicatorTitle}
            isOpen={CostIndicatorDrawerToggle}
            onClose={() => setCostIndicatorDrawerToggle(false)}
            position="right"
            size="200"
          >
            <div
              dangerouslySetInnerHTML={{
                __html: costIndicatorContent,
              }}
            />
          </Drawer>
          <Layout.Group
            css={CostIndicatorDrawerTextStyles(enableMapViewHideOption)}
            data-auto-testid={id}
            id={id}
            name={label}
            space={4}
          >
            {displayCostIndicator && (
              <CostIndicator
                enableMapViewHideOption={enableMapViewHideOption}
                indicatorData={costIndicator || ''}
                isGroupedLocationDrawer={isGroupedLocationDrawer}
                isNewMapViewDataCardDesign={isNewMapViewDataCardDesign}
              />
            )}
            {!enableMapViewHideOption && (
              <Button
                aria-label={label}
                css={infoCostIconButtonMobile}
                data-auto-testid="cost-indicator-info-icon"
                data-testid="cost-indicator-info-icon"
                onClick={() =>
                  setCostIndicatorDrawerToggle(
                    enableMapViewHideOption ? false : true
                  )
                }
                variant="text"
              >
                <IconSymbol icon="info" size={16} variant="outlined" />
              </Button>
            )}
          </Layout.Group>
          <p aria-hidden="true" style={{ display: 'none' }}>
            {label}
          </p>
        </React.Fragment>
      )}
    </React.Fragment>
  );
};
